import { PaginatedQueryResult } from '@dispatch/shared/hooks';
import { ResultStatus } from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridRowIdGetter,
  DataGridProProps,
  GridValidRowModel,
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import '../../styles/styles.css';
import { Empty } from './components/Empty';
import { Loader } from './components/Loader';
import { tableStyleHideSeparator, tableStyleOverrides } from './tableStyle';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';

interface Pagination {
  pageSize: number;
  pagingOptions?: number[];
}

type Action = {
  action: any;
  label: string;
  field: string;
  icon?: React.ReactElement;
};

export type ServerSidePagingProps<T> = Omit<
  PaginatedQueryResult<T, unknown>,
  'data' | 'error' | 'status' | 'refetch' | 'pages'
> & {
  onPageSizeChange: (rowsPerPage: number) => void;
};

export interface TableProps<T extends GridValidRowModel> {
  rows: T[];
  columns: GridColDef[];
  height?: number | string;
  actions?: Action[];
  status: ResultStatus;
  emptyEntityName: string;
  getRowId?: GridRowIdGetter<T>;
  serverPaging?: ServerSidePagingProps<T>;
  pagination?: Pagination;
  /**
   * Enable built-in client-side pagination (page/rows-per-page footer) for a
   * fully-materialized `rows` set. Opt-in and default off so existing
   * consumers — which scroll unless they wire `serverPaging` — are unchanged.
   */
  clientPagination?: boolean;
  tableState?: {
    columnModel: Record<string, boolean>;
    pinnedColumns: GridPinnedColumns | undefined;
  };
  handleShowColumns?: (newModel: GridColumnVisibilityModel) => void;
  handlePinnedColumns?: (newPinned: GridPinnedColumns) => void;
  handleSort?: (sortModel: GridSortModel) => void;
  dataGridProps?: Partial<DataGridProProps>;
  tableHeight?: string;
  initialState?: GridInitialStatePro;
  isAdjustable?: boolean;
}

export function TableComponent<T extends GridValidRowModel>({
  rows,
  columns,
  pagination = {
    pagingOptions: [5, 10, 15, 20],
    pageSize: 10,
  },
  actions = [],
  emptyEntityName,
  status,
  getRowId,
  serverPaging,
  clientPagination = false,
  tableState,
  handleShowColumns,
  handlePinnedColumns,
  handleSort,
  tableHeight,
  isAdjustable = true,
  ...rest
}: TableProps<T>) {
  const [cols, setColumns] = useState(columns);
  const [pageSize, setPageSize] = useState(pagination?.pageSize || 10);
  useEffect(() => {
    // add actions if they exist
    if (actions.length) {
      const actionsColumn = {
        field: 'actions',
        type: 'actions',
        width: 60,
        renderHeader: () => <MoreHorizIcon sx={{ color: 'text.secondary' }} />,
        getActions: (params: GridRowParams) => [...getActionItems(params)],
      };

      setColumns([...cols, actionsColumn]);
      return;
    }

    setColumns(columns);
  }, [actions.length, columns]);

  const getActionItems = (params: GridRowParams) => {
    return actions.map(({ action, label, field, icon }) => {
      return (
        <GridActionsCellItem
          data-testid="custom-element"
          icon={icon}
          label={label}
          onClick={() => action(params.row[field])}
          showInMenu
        />
      );
    });
  };

  const pageOptions = pagination?.pagingOptions
    ? [...(pagination?.pagingOptions || [])]
    : [];

  function getServerSidePagingOptions(): Partial<DataGridProProps<T>> {
    if (!serverPaging) {
      return {};
    }
    const count = serverPaging?.currentTotal || 0;
    return {
      localeText: {
        MuiTablePagination: {
          labelDisplayedRows: ({ from, to }) => {
            if (serverPaging?.hasNextPage) {
              return `${from} - ${to} of more than ${to}`;
            }
            return `${from} - ${to}`;
          },
        },
      },
      onPageSizeChange: (newPage) => {
        setPageSize(newPage);
        serverPaging?.onPageSizeChange(newPage);
        serverPaging?.goToFirstPage();
      },
      onPageChange: (number) => {
        if (number > serverPaging?.currentPageNumber) {
          serverPaging?.getNextPage();
        }
        if (number < serverPaging?.currentPageNumber) {
          serverPaging?.getPreviousPage();
        }
      },
      paginationMode: 'server',
      page: serverPaging?.currentPageNumber,
      rowCount: serverPaging?.hasNextPage ? count + 1 : count,
    };
  }

  const dataGridProps = {
    components: {
      MoreActionsIcon: MoreHorizIcon,
      NoRowsOverlay: () => (
        <Empty status={status} entityName={emptyEntityName} />
      ),
      NoResultsOverlay: () => (
        <Empty status="noresult" entityName={emptyEntityName} />
      ),
      LoadingOverlay: Loader,
    },
    sx: isAdjustable
      ? [tableStyleOverrides]
      : [tableStyleOverrides, tableStyleHideSeparator],
    ...rest.dataGridProps,
  };
  return (
    <div style={{ height: tableHeight ?? '70vh' }} data-testid="table-card">
      <DataGridPro
        {...dataGridProps}
        disableColumnFilter={true}
        getRowId={getRowId}
        loading={status === 'loading'}
        disableVirtualization={isRunningInJest()}
        rows={[...(rows || [])]}
        rowCount={rows.length}
        columns={cols}
        pageSize={pageSize}
        onPageSizeChange={(newPage) => {
          setPageSize(newPage);
        }}
        sortingMode="server"
        onSortModelChange={handleSort}
        rowsPerPageOptions={pageOptions}
        disableMultipleSelection
        pagination={!!serverPaging || clientPagination}
        columnVisibilityModel={tableState?.columnModel}
        pinnedColumns={tableState?.pinnedColumns}
        onColumnVisibilityModelChange={handleShowColumns}
        onPinnedColumnsChange={handlePinnedColumns}
        {...getServerSidePagingOptions()}
      />
    </div>
  );
}

export default TableComponent;
